'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';
import {
  holdBottomBarHidden,
  releaseBottomBarHidden,
} from '@/shared/lib/bottom-bar-reveal';
import { useSidebarStore } from '@/shared/stores/sidebar-store';
import { useOverlayScrollLock } from '@/shared/hooks/use-overlay-scroll-lock';
import { useBreakpoint } from '@/shared/hooks/use-media-query';
import { CONTENT_COLUMN_INLINE_START } from '@/shared/ui/DesktopShell';
import { BackButton } from './BackButton';

interface NestedSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

interface NestedSheetContentProps {
  children: React.ReactNode;
  className?: string;
}

interface NestedSheetHeaderProps {
  children: React.ReactNode;
  className?: string;
  trailingAction?: React.ReactNode;
  /**
   * First refusal on Back, for a panel that navigates INSIDE itself (the Brands
   * tree). Return `true` when the panel consumed it by moving up a level;
   * `false` (or omit the prop) closes the sheet. This is what keeps ONE back
   * control on screen — a panel with its own "< Back" row put two of them side
   * by side, one leaving and one going up, with nothing to tell them apart.
   */
  onBack?: () => boolean;
}

interface NestedSheetTitleProps {
  children: React.ReactNode;
  className?: string;
}

const NestedSheetContext = React.createContext<{
  onClose: () => void;
} | null>(null);

/**
 * Global context to track if any NestedSheet is open
 * Used by parent Sheet/Drawer to prevent closing on ESC when nested sheet is open
 */
interface NestedSheetStackContextType {
  hasOpenNestedSheet: boolean;
  registerNestedSheet: (id: string) => void;
  unregisterNestedSheet: (id: string) => void;
  /** Stacked (single-column) concept page: the parent has no bounded,
   *  positioned container to anchor an `absolute` panel, and the page itself
   *  is horizontally swipeable (concept prev/next). A sub-panel therefore
   *  renders as a full-viewport `fixed` overlay (portaled to body) that takes
   *  over the whole screen — Back in the top-left corner — instead of
   *  `absolute inset-0` inside the parent. Two-column (inspector aside is
   *  `h-full overflow-hidden`) keeps the absolute path, bounded to the column. */
  isStacked: boolean;
  /** Monotonic broadcast counter: bumping it asks every open NestedSheet to
   *  close itself. Used when a render is dispatched from inside a sub-panel —
   *  the user must land back on the concept view to SEE the render start. */
  closeSignal: number;
  /** Bump `closeSignal` to collapse all open sub-panels back to the main list. */
  closeAllNestedSheets: () => void;
  /** Two-column (non-stacked) only: the bounded inspector column registers
   *  itself here (via `ref={setPortalContainer}`) so EVERY drill-down NestedSheet
   *  portals into it and fills the WHOLE column — no matter how deep in the
   *  settings tree the sheet is declared. Without this a sheet anchored to its
   *  nearest positioned ancestor (e.g. the docked Explore/Refine toggle card's
   *  `relative`) covered only the FIRST settings group instead of the column.
   *  Null on the stacked page — there sub-panels portal to <body> instead. */
  portalContainer: HTMLElement | null;
  setPortalContainer: (el: HTMLElement | null) => void;
}

const NestedSheetStackContext =
  React.createContext<NestedSheetStackContextType>({
    hasOpenNestedSheet: false,
    registerNestedSheet: () => {},
    unregisterNestedSheet: () => {},
    isStacked: false,
    closeSignal: 0,
    closeAllNestedSheets: () => {},
    portalContainer: null,
    setPortalContainer: () => {},
  });

export function useNestedSheetStack() {
  return React.useContext(NestedSheetStackContext);
}

export function NestedSheetStackProvider({
  children,
  isStacked = false,
}: {
  children: React.ReactNode;
  isStacked?: boolean;
}) {
  const [openSheets, setOpenSheets] = React.useState<Set<string>>(new Set());

  // Broadcast close-all: each NestedSheet watches this counter and closes itself
  // when it changes (see NestedSheet below). A counter rather than a flag so it
  // works as a one-shot pulse — no reset/ack handshake needed.
  const [closeSignal, setCloseSignal] = React.useState(0);
  const closeAllNestedSheets = React.useCallback(() => {
    setCloseSignal((n) => n + 1);
  }, []);

  // Two-column drill-down anchor: the bounded inspector column registers itself
  // here so sub-panels portal into it (see NestedSheet below). A `useState`
  // rather than a ref so a NestedSheet re-renders into the portal once the
  // column mounts. `setPortalContainer` is a stable setter — safe as a ref
  // callback on the column div.
  const [portalContainer, setPortalContainer] =
    React.useState<HTMLElement | null>(null);

  const registerNestedSheet = React.useCallback((id: string) => {
    setOpenSheets((prev) => new Set(prev).add(id));
  }, []);

  const unregisterNestedSheet = React.useCallback((id: string) => {
    setOpenSheets((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // Stacked (single-column) concept page: while any sub-panel is open, hold the
  // floating bottom nav hidden so only the Generate capsule shows — the sub-panel
  // occupies the content area and the nav would otherwise sit stale-revealed over
  // it. Releasing hands ownership back to ConceptEditor's scroll-linked writer.
  // Mirrors Apple (sub-panel never drives the reveal) and Android
  // (`BottomBarRevealSubPanelGuard`). No-op off the stacked concept page.
  const hasOpenNestedSheet = openSheets.size > 0;
  React.useEffect(() => {
    if (!isStacked || !hasOpenNestedSheet) return;
    holdBottomBarHidden();
    return () => releaseBottomBarHidden();
  }, [isStacked, hasOpenNestedSheet]);

  const value = React.useMemo(
    () => ({
      hasOpenNestedSheet,
      registerNestedSheet,
      unregisterNestedSheet,
      isStacked,
      closeSignal,
      closeAllNestedSheets,
      portalContainer,
      setPortalContainer,
    }),
    [
      hasOpenNestedSheet,
      registerNestedSheet,
      unregisterNestedSheet,
      isStacked,
      closeSignal,
      closeAllNestedSheets,
      portalContainer,
    ]
  );

  return (
    <NestedSheetStackContext.Provider value={value}>
      {children}
    </NestedSheetStackContext.Provider>
  );
}

/**
 * NestedSheet - A sheet component that slides in from the right within parent container bounds
 * Used for secondary settings panels inside the main drawer
 */
export function NestedSheet({
  open,
  onOpenChange,
  children,
}: NestedSheetProps) {
  const {
    registerNestedSheet,
    unregisterNestedSheet,
    isStacked,
    closeSignal,
    portalContainer,
  } = useNestedSheetStack();
  const idRef = React.useRef(Math.random().toString(36).slice(2));

  // Close on a global close-all broadcast (Generate tapped from inside a
  // sub-panel). Track the previous value so this fires ONLY on an actual change,
  // never on mount — otherwise re-opening a sub-panel after an earlier Generate
  // (closeSignal already > 0) would slam it shut on the first frame.
  const prevCloseSignal = React.useRef(closeSignal);
  React.useEffect(() => {
    if (closeSignal !== prevCloseSignal.current) {
      prevCloseSignal.current = closeSignal;
      if (open) onOpenChange(false);
    }
  }, [closeSignal, open, onOpenChange]);

  // Register/unregister this nested sheet when it opens/closes
  React.useEffect(() => {
    if (open) {
      registerNestedSheet(idRef.current);
    } else {
      unregisterNestedSheet(idRef.current);
    }
  }, [open, registerNestedSheet, unregisterNestedSheet]);

  // Cleanup on unmount
  React.useEffect(() => {
    const id = idRef.current;
    return () => unregisterNestedSheet(id);
  }, [unregisterNestedSheet]);

  // Handle escape key
  React.useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChange]);

  const contextValue = React.useMemo(
    () => ({ onClose: () => onOpenChange(false) }),
    [onOpenChange]
  );

  // Stacked (single-column) concept page: render as a viewport `fixed` overlay
  // portaled to <body>, pinned BELOW the angle switcher via the
  // `--concept-content-top` variable the switcher publishes. Mounted only while
  // open, so the closed panel never sits off-screen where a horizontal page
  // swipe (concept prev/next) could drag it into view. Mirrors Apple/Android,
  // where the sub-panel occupies the content area under the pinned tabs and
  // scrolls internally.
  if (isStacked) {
    return (
      <NestedSheetContext.Provider value={contextValue}>
        {open && <StackedNestedSheet onClose={contextValue.onClose}>{children}</StackedNestedSheet>}
      </NestedSheetContext.Provider>
    );
  }

  // Two-column drill-down. `absolute inset-0` fills the nearest positioned
  // ancestor — but the section that owns this sheet can live arbitrarily deep
  // (e.g. inside the docked-toggle card's own `relative`), which would clip the
  // panel to that first group. So when the inspector column has registered
  // itself (`portalContainer`), portal the overlay+panel up into it: the
  // drill-down always fills the WHOLE column and covers the Generate footer.
  // Falls back to in-place `absolute` when no column is registered.
  const overlayAndPanel = (
    <>
      {/* Overlay - only visible when open, fills the bounded column */}
      <div
        className={cn(
          'absolute inset-0 z-40 bg-black/50 transition-opacity duration-300',
          open
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        )}
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />

      {/* Content panel - slides in from the trailing edge within the column
          bounds (mirrored in RTL).
          Apple parity: settings surface uses systemGroupedBackground
          (`bg-panel`, #f2f2f7) — identical to the page behind it — so the white
          SettingsCard groups read as grouped sections and the panel doesn't
          read as a lighter overlay than the page. */}
      <div
        className={cn(
          'absolute inset-0 z-50 bg-panel flex flex-col transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : 'translate-x-full rtl:-translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </>
  );

  return (
    <NestedSheetContext.Provider value={contextValue}>
      {portalContainer
        ? createPortal(overlayAndPanel, portalContainer)
        : overlayAndPanel}
    </NestedSheetContext.Provider>
  );
}

/**
 * Drill-down overlay used for the stacked (single-column) concept page. A
 * sub-panel takes over the CONTENT AREA — its own Back lands in the top-left
 * corner — parity with Android (hides the concept top bar) and Apple (hides the
 * nav bar). `overflow-hidden` clips the slide-in; the body scrolls internally.
 * Portaled to <body> so the page's horizontal swipe transform never moves or
 * reveals it.
 *
 * On lg+ the DesktopShell sidebar sits on the leading side of the single-column
 * content; the overlay is inset by `CONTENT_COLUMN_INLINE_START` so it covers ONLY the content
 * column and leaves the sidebar visible & interactive (Apple parity — see
 * Image: sidebar stays put while the Style panel fills the content area). Below
 * lg there's no sidebar (floating bottom nav), so the panel fills the full
 * viewport. Collapsing the sidebar (content goes full-width) drops the inset.
 */
function StackedNestedSheet({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  const [mounted, setMounted] = React.useState(false);
  // Slide in from the trailing edge on first paint: mount translated out, then
  // flip to 0 on the next frame so the CSS transition runs.
  const [entered, setEntered] = React.useState(false);

  // The panel covers the viewport, but the content column behind it kept its
  // own scrollbar — two bars side by side on a wide single-column window, and a
  // wheel gesture outside the panel scrolling a page nobody can see.
  useOverlayScrollLock();

  const sidebarCollapsed = useSidebarStore((s) => s.collapsed);
  const { isLg } = useBreakpoint();
  const leadingInset = isLg && !sidebarCollapsed ? CONTENT_COLUMN_INLINE_START : 0;

  React.useEffect(() => {
    setMounted(true);
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  if (!mounted || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-y-0 end-0 z-50 overflow-hidden"
      style={{ insetInlineStart: leadingInset }}
    >
      {/* Fully covered by the opaque panel below — kept so a tap on the brief
          slide-in gutter still closes the panel. */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={cn(
          // Inset the header below the device safe area (notch / status bar in
          // standalone PWA) so Back clears it; no-op in a normal browser tab.
          'absolute inset-0 bg-panel flex flex-col pt-[env(safe-area-inset-top)] transition-transform duration-300 ease-out',
          entered ? 'translate-x-0' : 'translate-x-full rtl:-translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

export function NestedSheetContent({
  children,
  className,
}: NestedSheetContentProps) {
  return (
    <div className={cn('flex flex-col h-full overflow-hidden', className)}>
      {children}
    </div>
  );
}

export function NestedSheetHeader({
  children,
  className,
  trailingAction,
  onBack,
}: NestedSheetHeaderProps) {
  const context = React.useContext(NestedSheetContext);
  const t = useTranslations('common');

  return (
    <div
      className={cn(
        // Transparent toolbar, no separator line. 3-column flex grid keeps the
        // centered title balanced against the chevron-only Back control.
        'flex items-center gap-2 px-2 py-2 shrink-0',
        className
      )}
    >
      <div className="flex items-center justify-start min-w-0 flex-1">
        <BackButton
          size="compact"
          onClick={() => {
            if (onBack?.()) return;
            context?.onClose();
          }}
          label={t('back')}
        />
      </div>

      <div className="flex items-center gap-1 shrink-0 text-subpanel-title min-w-0 truncate">
        {children}
      </div>

      <div className="flex items-center justify-end min-w-0 flex-1">
        {trailingAction}
      </div>
    </div>
  );
}

export function NestedSheetTitle({
  children,
  className,
}: NestedSheetTitleProps) {
  return (
    <h2 className={cn('text-subpanel-title text-foreground', className)}>
      {children}
    </h2>
  );
}

export function NestedSheetBody({
  children,
  className,
  capFormWidth = true,
}: {
  children: React.ReactNode;
  className?: string;
  /** Cap the content to the readable column width (max-w-[560px]) and center it
   *  so sub-panels don't stretch edge-to-edge on the wide single-column
   *  full-viewport overlay. ON by default for ALL sub-panels — forms (rows/
   *  fields) AND tile/grid pickers (styles, materials, colors, output) — so
   *  every drill-down reads at the same width as the Model panel (consistency
   *  was the point: grids used to sprawl full-bleed and looked oversized on
   *  medium screens). No-op in the narrow two-column inspector (already
   *  narrower than the cap). Pass `capFormWidth={false}` for the rare panel that
   *  genuinely needs full bleed. Mirrors Apple/Android. */
  capFormWidth?: boolean;
}) {
  return (
    // `pb-8`: the panel's last element — a footnote, a caveat, the final row —
    // must not sit flush against the bottom edge (or, on the stacked overlay,
    // under the floating nav). Every sub-panel had the same clipped tail, so
    // the breathing room lives here rather than in each one.
    <div className={cn('flex-1 overflow-y-auto pb-8', className)}>
      {capFormWidth ? (
        <div className="mx-auto w-full max-w-[560px]">{children}</div>
      ) : (
        children
      )}
    </div>
  );
}
